import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';

// COMPONENTS
import Button from '../../components/Button';
import GlobalModal from '../../components/GlobalModal';
import VideoModal from './VideoModal';
import FormCheckBox from '../../components/FormCheckBox';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { TrainingModuleEditCommand } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const ContentTrainingModuleDetail: React.FC = ({}) => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Getting the route id
	const params = useParams<RouteParams>();

	// Get all Standards from Redux store
	const childSafeStandards = useAppSelector((state) => state.lists.childSafeStandards);

	// Get all Payment Plans from Redux store
	const paymentPlans = useAppSelector((state) => state.lists.paymentPlans);

	// Set up data state and initial data
	const initialData: TrainingModuleEditCommand = {
		id: undefined,
		order: undefined,
		title: undefined,
		subtitle: undefined,
		overview: undefined,
		videoEmbedCode: undefined,
		selectedChildSafeStandards: [],
		selectedPlans: []
	};

	const [moduleData, setModuledata] = React.useState<TrainingModuleEditCommand>(initialData);

	// State to control Modal
	const [showVideoModal, setShowVideoModal] = React.useState<boolean>(false);

	// function to  Get training modules data from api call
	const getTrainingModulesDetailData = async () => {
		if (params.id) {
			setIsLoading(true);

			const response = await apibridge.getTrainingModuleEdit(params.id);

			response.result = response.result || initialData;

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				setModuledata(response.result);
			}
		} else {
			setIsLoading(false);

			setModuledata(initialData);
		}
	};

	React.useEffect(() => {
		getTrainingModulesDetailData();
	}, []);

	const onSubmit = async (values: any) => {
		setIsLoading(true);

		const trainingData: TrainingModuleEditCommand = {
			id: values.id,
			order: values.order,
			title: values.titleInput,
			subtitle: values.subtitleInput,
			overview: values.overviewInput,
			videoEmbedCode: values.videoEmbedCode,
			selectedChildSafeStandards: values.childSafetyStandardsInput,
			selectedPlans: values.selectedPlansInput
		};

		const response = await apibridge.postTrainingModuleEdit(trainingData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/content-training-module');
		}
	};

	const validationSchema = Yup.object({
		titleInput: Yup.string().required('Required'),
		subtitleInput: Yup.string().required('Required'),
		overviewInput: Yup.string().required('Required'),
		childSafetyStandardsInput: Yup.array().min(1, 'Required'),
		selectedPlansInput: Yup.array().min(1, 'Required')
		// videoEmbedCode: Yup.string().required('Required')
	});

	const initialValues = {
		id: moduleData.id || '',
		order: moduleData.order || 1,
		titleInput: moduleData.title || '',
		subtitleInput: moduleData.subtitle || '',
		overviewInput: moduleData.overview || '',
		videoEmbedCode: moduleData.videoEmbedCode || '',
		childSafetyStandardsInput: moduleData.selectedChildSafeStandards || [],
		selectedPlansInput: moduleData.selectedPlans || []
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						{!!formErrorMessage ? (
							<div className="form-validation-error">{formErrorMessage}</div>
						) : (
							<>
								{isLoading ? (
									<LoadingSpinner show={isLoading} />
								) : (
									<>
										<div className="general-form-layout-wrapper">
											<div className="form-main">
												<div className="form-group">
													<label htmlFor="titleInput" className="label">
														Training module title
													</label>

													<div className="form-field">
														<Field name="titleInput" id="titleInput" type="text" className="form-input" />

														<ErrorMessage name="titleInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="subtitleInput" className="label">
														Training module subtitle
													</label>

													<div className="form-field">
														<Field name="subtitleInput" id="subtitleInput" type="text" className="form-input" />

														<ErrorMessage name="subtitleInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="overviewInput" className="label">
														Overview text
													</label>

													<div className="form-field">
														<Field
															as="textarea"
															name="overviewInput"
															id="overviewInput"
															type="text"
															className="form-input"
														/>

														<ErrorMessage name="overviewInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="videoEmbedCode" className="label">
														Video URL
													</label>

													<p className="sub-grey-text small">
														Supported video formats from either Vimeo or YouTube only.
													</p>

													<div className="form-field">
														<Field
															as="textarea"
															name="videoEmbedCode"
															id="videoEmbedCode"
															type="text"
															className="form-input"
														/>

														<ErrorMessage name="videoEmbedCode" component="div" className="error-message" />

														{formik.values.videoEmbedCode && (
															<Button className="btn-text" onClick={() => setShowVideoModal(true)}>
																Preview video
															</Button>
														)}
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="childSafetyStandardsInput" className="label mbtom-16">
														Which Child Safe Standard(s) does this module cover? Select all that apply
													</label>

													<div className="form-field">
														<FormCheckBox
															values={moduleData.selectedChildSafeStandards || []}
															options={childSafeStandards}
															onChange={(vals: string[]) => formik.setFieldValue('childSafetyStandardsInput', vals)}
														/>

														<ErrorMessage name="childSafetyStandardsInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="selectedPlansInput" className="label mbtom-16 mtop-24">
														Organisations with access
													</label>

													<div className="form-field">
														<FormCheckBox
															values={moduleData.selectedPlans || []}
															options={paymentPlans}
															onChange={(vals: string[]) => formik.setFieldValue('selectedPlansInput', vals)}
														/>

														<ErrorMessage name="selectedPlansInput" component="div" className="error-message" />
													</div>
												</div>
											</div>
										</div>

										<div className="general-divider"></div>

										<div className="form-button-group">
											<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
												Save
											</Button>
										</div>

										<GlobalModal
											isOpen={showVideoModal}
											setIsOpen={setShowVideoModal}
											titleText="Video embed code preview"
										>
											<VideoModal videoEmbedCode={formik.values.videoEmbedCode || ''} />
										</GlobalModal>
									</>
								)}
							</>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export default ContentTrainingModuleDetail;
